import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import API_BASE_URL from '../utils/config';

const BlogSection = () => {
  const navigate = useNavigate();
  const [apiBlogs, setApiBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hardcoded blog data
  const staticBlogs = [
    {
        id: 1,
        title: 'Best Universities for PhD in Dehradun',
        description:
            'Civil engineering is often referred to as the backbone of modern infrastructure and development.',
        image: '', // Empty image URL to test fallback
        url: '/blog/phd-programs', // Custom URL for this specific blog post
    },
    {
        id: 2,
        title: 'B Tech in civil engineering in Uttarakhand',
        description:
            'A Doctor of Philosophy (PhD) is the highest academic degree, intended for people who want to make a greater contribution to their specific field through original and comprehensive research; the students who complete this get the designation of doctor (Dr.).',
        image: '', // Empty image URL to test fallback
        url: '/blog/B.Tech-in-Civil-Engineering-in-Uttarakhand-Colleges', // Custom URL for this specific blog post
    },
  ];

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/blogs`);
        if (!response.ok) {
          throw new Error('Failed to fetch blogs');
        }
        const data = await response.json();
        setApiBlogs(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
      },
    },
  };

  // Custom university colors
  const primaryColor = '#0e4d38';
  const secondaryColor = '#f8f9fa';
  const accentColor = '#0a3b2b';

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <section className="w-full py-16 bg-gray-50 font-sans mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800">
            <span className="inline-block pb-2 border-b-4 border-green-700">Blogs</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Stay connected with insights, stories, and innovations from the Maya Devi University community
          </p>
        </motion.div>

        {/* Blog Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
        >
          {/* Static Blogs */}
          {staticBlogs.map((blog) => (
            <motion.div
              key={blog.id}
              variants={cardVariants}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              onClick={() => navigate(blog.url)}
              style={{ cursor: 'pointer' }}
            >
              {/* Blog Thumbnail or University Name */}
              {blog.image ? (
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
              ) : (
                <div 
                  className="w-full h-48 flex items-center justify-center bg-green-700 text-white font-bold text-xl md:text-2xl p-4 text-center"
                >
                  MAYA DEVI UNIVERSITY
                </div>
              )}
              
              {/* Blog Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800 line-clamp-2 min-h-14">
                  {blog.title}
                </h3>
                <p className="text-base text-gray-600 mb-6 line-clamp-3 min-h-20">
                  {blog.description}
                </p>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  className="flex justify-end"
                >
                  <span
                    className="inline-block px-6 py-2 rounded-full text-white font-medium text-sm transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Read More
                  </span>
                </motion.div>
              </div>
            </motion.div>
          ))}

          {/* API Blogs */}
          {apiBlogs.map((blog) => (
            <motion.div
              key={blog._id}
              variants={cardVariants}
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              onClick={() => navigate(`/blog/${blog._id}`)}
              style={{ cursor: 'pointer' }}
            >
              {/* Blog Thumbnail or University Name */}
              {blog.image_url ? (
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
              ) : (
                <div 
                  className="w-full h-48 flex items-center justify-center bg-green-700 text-white font-bold text-xl md:text-2xl p-4 text-center"
                >
                  MAYA DEVI UNIVERSITY
                </div>
              )}
              
              {/* Blog Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-gray-800 line-clamp-2 min-h-14">
                  {blog.title}
                </h3>
                <p className="text-base text-gray-600 mb-6 line-clamp-3 min-h-20">
                  {blog.description}
                </p>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  className="flex justify-end"
                >
                  <span
                    className="inline-block px-6 py-2 rounded-full text-white font-medium text-sm transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Read More
                  </span>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      <Footer />
    </section>
  );
};

export default BlogSection;